import api from "./api";
import logger from "../utils/logger";

const photoService = {
  /**
   * Request a presigned upload URL for a photo
   * @param {number} bookingId - Booking ID
   * @param {Object} data - Upload request data
   * @param {string} data.filename - Original filename
   * @param {string} data.contentType - MIME type
   * @param {'booking'|'completion'} data.context - Upload context
   * @returns {Promise<Object>} { uploadUrl, photoId, key, expiresIn }
   */
  getPresignedUrl: async (bookingId, { filename, contentType, context }) => {
    const response = await api.post(
      `/bookings/${bookingId}/photos/upload-url`,
      { filename, contentType, context },
    );
    return response.data;
  },

  /**
   * Upload a file directly to R2 using a presigned URL
   * @param {string} uploadUrl - Presigned PUT URL
   * @param {File} file - File object to upload
   * @returns {Promise<Response>} Fetch response
   */
  uploadToR2: async (uploadUrl, file) => {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }

    return response;
  },

  /**
   * Confirm that a photo was successfully uploaded
   * @param {number} bookingId - Booking ID
   * @param {string} photoId - Photo UUID from presigned URL step
   * @returns {Promise<Object>} Confirmed photo data
   */
  confirmUpload: async (bookingId, photoId) => {
    const response = await api.post(`/bookings/${bookingId}/photos/confirm`, {
      photoId,
    });
    return response.data;
  },

  /**
   * Upload a single photo (presign → R2 → confirm)
   * @param {number} bookingId - Booking ID
   * @param {File} file - File to upload
   * @param {'booking'|'completion'} context - Upload context
   * @returns {Promise<Object>} Confirmed photo data
   */
  uploadPhoto: async (bookingId, file, context) => {
    // 1. Get presigned URL
    const { uploadUrl, photoId } = await photoService.getPresignedUrl(
      bookingId,
      { filename: file.name, contentType: file.type, context },
    );

    // 2. Upload directly to R2
    await photoService.uploadToR2(uploadUrl, file);

    // 3. Confirm
    const photo = await photoService.confirmUpload(bookingId, photoId);
    return photo;
  },

  /**
   * Upload multiple photos in parallel
   * @param {number} bookingId - Booking ID
   * @param {File[]} files - Array of File objects
   * @param {'booking'|'completion'} context - Upload context
   * @returns {Promise<{ succeeded: Object[], failed: Error[] }>}
   */
  uploadAllPhotos: async (bookingId, files, context) => {
    const results = await Promise.allSettled(
      files.map((file) => photoService.uploadPhoto(bookingId, file, context)),
    );

    const succeeded = results
      .filter((r) => r.status === "fulfilled")
      .map((r) => r.value);
    const failed = results
      .filter((r) => r.status === "rejected")
      .map((r) => r.reason);

    if (failed.length > 0) {
      logger.error(`${failed.length} photo(s) failed to upload:`, failed);
    }

    return { succeeded, failed };
  },

  /**
   * Fetch photos for a booking
   * @param {number} bookingId - Booking ID
   * @param {'booking'|'completion'} [context] - Optional filter by context
   * @returns {Promise<Object[]>} Array of photo objects
   */
  getPhotos: async (bookingId, context) => {
    const params = context ? { context } : {};
    const response = await api.get(`/bookings/${bookingId}/photos`, { params });
    return response.data.photos;
  },

  /**
   * Delete a photo
   * @param {number} bookingId - Booking ID
   * @param {string} photoId - Photo UUID
   * @returns {Promise<void>}
   */
  deletePhoto: async (bookingId, photoId) => {
    await api.delete(`/bookings/${bookingId}/photos/${photoId}`);
  },
};

export default photoService;
